import React, { useState, useEffect } from 'react';
import { X, Car, Bike, ExternalLink, RotateCcw, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Event, EventCategory } from '../../hooks/useEvents';
import { formatCurrency } from '../../lib/currencyUtils';
import { formatRegistrationDeadline } from '../../utils/dateUtils';
import { getUserReEntryStatus, registerReEntry, checkReEntryEligibility, ReEntryStatus, deletePendingReEntryRegistration, cancelReEntryRegistration, deleteReEntryRegistration } from '../../lib/reEntryUtils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface EventDetailsModalProps {
  selectedEvent: Event;
  isEventUpcoming: (eventDate: string) => boolean;
  isRegistrationOpen: (eventDate: string) => boolean;
  formatDateForDisplay: (date: string) => string;
  selectedVehicleFilter: 'car' | 'bike';
  setSelectedVehicleFilter: (filter: 'car' | 'bike') => void;
  registrations: any[];
  handleCategorySelect: (category: EventCategory) => void;
  handleUnregister: (registrationId: string, categoryName: string) => void;
  getEventTotalAmountToPay: (eventId: string) => number;
  onClose: () => void;
  getVehicleIcon: (vehicleType: string) => JSX.Element;
  handleTicketShopClick?: (url: string) => void;
  refreshRegistrations?: () => Promise<void>;
}

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  selectedEvent,
  isEventUpcoming,
  isRegistrationOpen,
  formatDateForDisplay,
  selectedVehicleFilter,
  setSelectedVehicleFilter,
  registrations,
  handleCategorySelect,
  handleUnregister,
  getEventTotalAmountToPay,
  onClose,
  getVehicleIcon,
  handleTicketShopClick,
  refreshRegistrations
}) => {
  const { user } = useAuth();
  const [reEntryStatuses, setReEntryStatuses] = useState<Record<string, ReEntryStatus>>({});
  const [reEntryEligibility, setReEntryEligibility] = useState<Record<string, any>>({});
  const [showReEntryModal, setShowReEntryModal] = useState(false);
  const [selectedCategoryForReEntry, setSelectedCategoryForReEntry] = useState<EventCategory | null>(null);
  const [isRegisteringReEntry, setIsRegisteringReEntry] = useState(false);
  const [loadingReEntryData, setLoadingReEntryData] = useState<Record<string, boolean>>({});

  // Filter categories based on selected vehicle type
  const getFilteredCategories = (categories: EventCategory[]) => {
    return categories.filter(category => category.vehicle_type === selectedVehicleFilter);
  };

  // Load re-entry statuses and eligibility only for categories where user is registered
  useEffect(() => {
    if (user?.id) {
      const loadReEntryData = async () => {
        // Only load for categories where user has a registration
        const userRegisteredCategories = selectedEvent.event_categories.filter(category => 
          registrations.some(reg => 
            reg.event_id === selectedEvent.id && 
            reg.category_id === category.id && 
            reg.user_id === user.id
          )
        );

        const statuses: Record<string, ReEntryStatus> = {};
        const eligibility: Record<string, any> = {};
        const loadingStates: Record<string, boolean> = {};

        // Set loading state for relevant categories
        userRegisteredCategories.forEach(category => {
          loadingStates[category.id] = true;
        });
        setLoadingReEntryData(loadingStates);

        // Load data in parallel for better performance
        const promises = userRegisteredCategories.map(async (category) => {
          try {
            const [status, eligibilityData] = await Promise.all([
              getUserReEntryStatus(user.id, selectedEvent.id, category.id),
              checkReEntryEligibility(user.id, selectedEvent.id, category.id)
            ]);
            
            return {
              categoryId: category.id,
              status,
              eligibility: eligibilityData
            };
          } catch (error) {
            console.error(`Error loading re-entry data for category ${category.id}:`, error);
            return {
              categoryId: category.id,
              status: null,
              eligibility: null
            };
          }
        });

        const results = await Promise.all(promises);
        
        results.forEach(({ categoryId, status, eligibility: eligibilityData }) => {
          if (status) statuses[categoryId] = status;
          if (eligibilityData) eligibility[categoryId] = eligibilityData;
        });

        setReEntryStatuses(statuses);
        setReEntryEligibility(eligibility);
        
        // Clear loading states
        setLoadingReEntryData({});
      };
      
      loadReEntryData();
    }
  }, [user?.id, selectedEvent.id, registrations]);

  const handleReEntryRegistration = async () => {
    if (!selectedCategoryForReEntry || !user?.id) return;

    setIsRegisteringReEntry(true);
    try {
      await registerReEntry(user.id, selectedEvent.id, selectedCategoryForReEntry.id);
      toast.success('Re-entry registration successful!');
      setShowReEntryModal(false);
      setSelectedCategoryForReEntry(null);
      
      // Refresh re-entry statuses
      const newStatus = await getUserReEntryStatus(user.id, selectedEvent.id, selectedCategoryForReEntry.id);
      setReEntryStatuses(prev => ({
        ...prev,
        [selectedCategoryForReEntry.id]: newStatus
      }));

      // Refresh registrations data for real-time UI update
      if (refreshRegistrations) {
        await refreshRegistrations();
      }
    } catch (error) {
      console.error('Error registering for re-entry:', error);
      toast.error('Failed to register for re-entry');
    } finally {
      setIsRegisteringReEntry(false);
    }
  };

  const handleUndoReEntry = async (categoryId: string) => {
    if (!user?.id) return;
    // Find the pending re-entry registration for this user/event/category
    const pendingRegistration = registrations.find(
      reg => reg.event_id === selectedEvent.id && reg.category_id === categoryId && reg.original_registration_id && reg.status === 'pending' && reg.user_id === user.id
    );
    if (!pendingRegistration) {
      toast.error('No pending re-entry registration found to undo.');
      return;
    }
    try {
      await deletePendingReEntryRegistration(pendingRegistration.id, user.id);
      toast.success('Pending re-entry registration undone.');
      // Refresh re-entry statuses
      const newStatus = await getUserReEntryStatus(user.id, selectedEvent.id, categoryId);
      setReEntryStatuses(prev => ({
        ...prev,
        [categoryId]: newStatus
      }));
    } catch (error) {
      toast.error('Failed to undo re-entry registration.');
    }
  };

  const getReEntryStatusDisplay = (categoryId: string) => {
    const status = reEntryStatuses[categoryId];
    const isLoading = loadingReEntryData[categoryId];

    // Show loading state
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading re-entry status...</span>
        </div>
      );
    }

    if (!status) return null;

    if (status.has_completed_re_entry) {
      return (
        <div className="flex items-center gap-2 text-green-400 text-sm">
          <CheckCircle className="h-4 w-4" />
          <span>Re-entry Ready to Compete</span>
        </div>
      );
    }

    if (status.has_pending_re_entry) {
      return (
        <div className="flex items-center gap-2 text-yellow-400 text-sm">
          <Clock className="h-4 w-4" />
          <span>Re-entry Pending Approval</span>
        </div>
      );
    }

    if (status.can_request_re_entry) {
      const eligibility = reEntryEligibility[categoryId];
      const reEntryFee = eligibility?.re_entry_fee || 0;
      return (
        <button
          onClick={() => {
            setSelectedCategoryForReEntry(selectedEvent.event_categories.find(c => c.id === categoryId)!);
            setShowReEntryModal(true);
          }}
          className="flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm"
        >
          <RotateCcw className="h-4 w-4" />
          <span>Re-enter - {formatCurrency(reEntryFee)}</span>
        </button>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">{selectedEvent.name}</h2>
            {!isEventUpcoming(selectedEvent.event_date) && (
              <span className="inline-block mt-2 px-3 py-1 bg-gray-600 text-white text-sm rounded-full">
                Past Event
              </span>
            )}
          </div>
          <button onClick={onClose}>
            <X className="h-6 w-6 text-gray-400 hover:text-white" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Event Details</h3>
            <p className="text-gray-400 mb-4">{selectedEvent.description}</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                  <line x1="16" x2="16" y1="2" y2="6" />
                  <line x1="8" x2="8" y1="2" y2="6" />
                  <line x1="3" x2="21" y1="10" y2="10" />
                </svg>
                <span>Event Date: {formatDateForDisplay(selectedEvent.event_date)}</span>
                {!isEventUpcoming(selectedEvent.event_date) && (
                  <span className="text-gray-500">(Past)</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>Registration closes: {formatRegistrationDeadline(selectedEvent.event_date)}</span>
              </div>
              
              {/* Ticket Shop Link */}
              {selectedEvent.ticket_shop_url && (
                <div className="flex items-center gap-2 text-primary-400 mt-2">
                  <ExternalLink className="h-4 w-4" />
                  <button 
                    onClick={() => handleTicketShopClick && handleTicketShopClick(selectedEvent.ticket_shop_url!)}
                    className="hover:underline"
                  >
                    Buy Tickets Online
                  </button>
                </div>
              )}
            </div>
          </div>

          {selectedEvent.track_image?.url && (
            <div className="aspect-video">
              <img
                src={selectedEvent.track_image.url}
                alt={selectedEvent.name}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          )}
        </div>

        {/* Vehicle Type Filter Tabs */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">Racing Categories</h3>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSelectedVehicleFilter('car')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                selectedVehicleFilter === 'car'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              <Car className="h-4 w-4" />
              Cars ({selectedEvent.event_categories.filter(c => c.vehicle_type === 'car').length})
            </button>
            <button
              onClick={() => setSelectedVehicleFilter('bike')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                selectedVehicleFilter === 'bike'
                  ? 'bg-primary-600 text-white'
                  : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              <Bike className="h-4 w-4" />
              Bikes ({selectedEvent.event_categories.filter(c => c.vehicle_type === 'bike').length})
            </button>
          </div>
        </div>

        {/* Filtered Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {getFilteredCategories(selectedEvent.event_categories).map((category) => {
            const registration = registrations.find(
              reg => reg.event_id === selectedEvent.id && reg.category_id === category.id
            );
            const eventIsUpcoming = isEventUpcoming(selectedEvent.event_date);
            const regOpen = isRegistrationOpen(selectedEvent.event_date);
            const isPaid = registration?.payment_status === 'paid';
            
            return (
              <div key={category.id} className="bg-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                    {getVehicleIcon(category.vehicle_type)}
                    {category.name}
                  </h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    category.vehicle_type === 'car'
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-primary-100 text-primary-800'
                  }`}>
                    {category.vehicle_type}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-400 mb-4">
                  <p><strong>Time Range:</strong> {category.time_range}</p>
                  <p><strong>Format:</strong> {category.tree_format}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-gray-400">Registration Fee</p>
                    <p className="text-white font-semibold">{formatCurrency(category.registration_fee)}</p>
                  </div>
                  {category.use_percentage_prizes ? (
                    <div>
                      <p className="text-gray-400">Prize Structure</p>
                      <p className="text-green-500 font-semibold">
                        {category.first_place_percentage}% / {category.second_place_percentage}% split
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-400">1st Place Prize</p>
                      <p className="text-green-500 font-semibold">{formatCurrency(category.first_place_prize)}</p>
                    </div>
                  )}
                </div>

                {registration ? (
                  <div className="space-y-2">
                    <button
                      onClick={async () => {
                        if (!user) {
                          toast.error('You must be logged in to unregister.');
                          return;
                        }
                        if (registration.original_registration_id) {
                          // Use deleteReEntryRegistration for re-entries
                          try {
                            await deleteReEntryRegistration(registration.id, user.id);
                            toast.success('Re-entry registration removed.');
                            const newStatus = await getUserReEntryStatus(user.id, selectedEvent.id, category.id);
                            setReEntryStatuses(prev => ({ ...prev, [category.id]: newStatus }));
                            
                            // Refresh registrations data for real-time UI update
                            if (refreshRegistrations) {
                              await refreshRegistrations();
                            }
                          } catch (error) {
                            toast.error('Failed to remove re-entry registration.');
                          }
                        } else {
                          // Normal unregister
                          handleUnregister(registration.id, category.name);
                          
                          // Refresh registrations data for real-time UI update
                          if (refreshRegistrations) {
                            await refreshRegistrations();
                          }
                        }
                      }}
                      disabled={!regOpen || isPaid}
                      className={`w-full ${
                        isPaid
                          ? 'bg-green-600 cursor-not-allowed opacity-80'
                          : 'bg-red-600 hover:bg-red-700'
                      } text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isPaid
                        ? '✓ Registered & Paid'
                        : !regOpen
                          ? !eventIsUpcoming
                            ? '✓ Registered (Past Event)'
                            : '✓ Registered (Registration Closed)'
                          : registration.original_registration_id
                            ? 'Cancel Re-Enter'
                            : 'Unregister'}
                    </button>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">Payment Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        registration.payment_status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {registration.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </div>
                    {registration.original_registration_id && (
                      <div className="text-xs text-orange-400 font-semibold">Re-entry Registration</div>
                    )}
                    {/* Re-entry Status */}
                    {getReEntryStatusDisplay(category.id)}
                  </div>
                ) : (
                  <button
                    onClick={() => handleCategorySelect(category)}
                    disabled={!regOpen}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {regOpen
                      ? `Register - ${formatCurrency(category.registration_fee)}`
                      : !eventIsUpcoming
                        ? 'Registration Closed (Past Event)'
                        : 'Registration Closed'
                    }
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Show message if no categories match filter */}
        {getFilteredCategories(selectedEvent.event_categories).length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No {selectedVehicleFilter} categories available for this event.</p>
          </div>
        )}

        {/* Event-Specific Total Payment Section */}
        {getEventTotalAmountToPay(selectedEvent.id) > 0 && (
          <div className="mt-8 pt-6 border-t border-zinc-700">
            <div className="bg-zinc-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xl font-semibold text-white">Total Amount to Pay for this Event:</span>
                <span className="text-3xl font-bold text-primary-500">{formatCurrency(getEventTotalAmountToPay(selectedEvent.id))}</span>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                This includes all pending registrations for {selectedEvent.name}
              </p>
              <button className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold text-lg">
                Pay Now - {formatCurrency(getEventTotalAmountToPay(selectedEvent.id))}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Payment functionality will be implemented soon
              </p>
            </div>
          </div>
        )}
        
        {/* Ticket Shop Link */}
        {selectedEvent.ticket_shop_url && (
          <div className="mt-6 pt-6 border-t border-zinc-700">
            <button
              onClick={() => handleTicketShopClick && handleTicketShopClick(selectedEvent.ticket_shop_url!)}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
            >
              <ExternalLink className="h-5 w-5" />
              Buy Tickets Online
            </button>
          </div>
        )}

        {/* Re-entry Registration Modal */}
        {showReEntryModal && selectedCategoryForReEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Register for Re-entry</h3>
                <button onClick={() => setShowReEntryModal(false)}>
                  <X className="h-5 w-5 text-gray-400 hover:text-white" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-gray-300 mb-2">
                    Registering for re-entry in <span className="text-white font-medium">{selectedCategoryForReEntry.name}</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    You can re-enter once per category. This will create a new registration that needs admin approval.
                  </p>
                  {reEntryEligibility[selectedCategoryForReEntry.id]?.re_entry_fee > 0 && (
                    <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Re-entry Fee:</span>
                        <span className="text-xl font-bold text-primary-500">
                          {formatCurrency(reEntryEligibility[selectedCategoryForReEntry.id].re_entry_fee)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowReEntryModal(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReEntryRegistration}
                  disabled={isRegisteringReEntry}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRegisteringReEntry ? 'Registering...' : 'Register for Re-entry'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailsModal;